import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';

export interface SortSelection {
  sort: string;
  order: string;
}

export interface SortSelectionSource {
  sort: MaybeRefOrGetter<string | undefined>;
  order: MaybeRefOrGetter<string | undefined>;
  isActive?: MaybeRefOrGetter<boolean>;
}

const noSelection = (): SortSelection => ({ sort: '', order: '' });

export default function useSortSelection(source?: SortSelectionSource | null) {
  const localSelection = ref<SortSelection | null>(null);

  const isDerived = computed(() => (source ? toValue(source.isActive ?? true) : false));

  const derivedSelection = computed<SortSelection | null>(() => {
    if (!source) return null;

    const sort = toValue(source.sort);
    const order = toValue(source.order);

    return sort && order ? { sort, order } : null;
  });

  const selected = computed<SortSelection | null>(() =>
    isDerived.value ? derivedSelection.value : localSelection.value
  );

  const isSelected = computed(() => selected.value !== null);

  const select = (sort: string, order: string): SortSelection => {
    const isAlreadySelected = selected.value?.sort === sort && selected.value?.order === order;
    const next = isAlreadySelected ? noSelection() : { sort, order };

    if (!isDerived.value) {
      localSelection.value = isAlreadySelected ? null : next;
    }

    return next;
  };

  return {
    selected,
    isSelected,
    select,
  };
}
